import { useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { FREQUENCY } from '../../models/DonationFrequency';
import { AMOUNT_INPUT } from '../../models/DonationAmount';
import { formatCurrency } from '../../features/passport/formatCurrency';
import { getCurrencySymbol } from '../../features/passport/getCurrencySymbol';
const findDefaultAmount = amounts => amounts.filter(amount => amount.isVisible && amount.isDefault)[0];
const getOtherAmount = amounts => amounts.find(amount => amount.name === AMOUNT_INPUT.OTHER && amount.isVisible && amount.amount > 0);
const formatOtherAmountValue = (otherAmount, currencyCode, userLocale) => formatCurrency({
  amount: otherAmount,
  currencyCode,
  options: {
    currencyDisplay: 'code'
  },
  locale: userLocale
}).replace(currencyCode, '').trim();
export const createInitialState = _ref => {
  let {
    donationFrequencies,
    donationAmounts,
    recurringAmounts,
    currencyCode,
    userLocale
  } = _ref;
  const defaultFrequency = donationFrequencies.filter(frequency => frequency.isVisible && frequency.isDefault)[0];
  const currentPresets = defaultFrequency.name === FREQUENCY.ONE_TIME ? donationAmounts : recurringAmounts;
  const defaultAmount = findDefaultAmount(currentPresets);
  const otherAmount = getOtherAmount(currentPresets);
  return {
    frequency: defaultFrequency.name,
    amount: defaultAmount?.amount > 0 ? defaultAmount.amount : null,
    currencySymbol: getCurrencySymbol(currencyCode, userLocale),
    currencyCode,
    activeInput: defaultAmount?.name ?? null,
    otherAmountValue: otherAmount ? otherAmount.amount : null,
    otherAmountDisplayValue: otherAmount ? formatOtherAmountValue(otherAmount.amount, currencyCode, userLocale) : '',
    userLocale,
    recurringEndDate: '',
    error: ''
  };
};
export const useDonationBlockReducer = _ref2 => {
  let {
    donationFrequencies,
    donationAmounts,
    recurringAmounts,
    currencyCode,
    userLocale
  } = _ref2;
  const {
    t
  } = useTranslation('blocks.donation');
  const DonationReducer = (state, action) => {
    switch (action.type) {
      case 'SET_ENTIRE_BLOCK_STATE':
        {
          return action.newBlockState;
        }
      case 'SET_CURRENCY_CODE':
        {
          return {
            ...state,
            currencyCode: action.currencyCode,
            currencySymbol: getCurrencySymbol(action.currencyCode, state.userLocale)
          };
        }
      case 'SET_USER_LOCALE':
        {
          return {
            ...state,
            userLocale: action.userLocale
          };
        }
      case 'SELECT_FREQUENCY_BUTTON':
        {
          const {
            frequency,
            presets
          } = action;
          if (state.activeInput === AMOUNT_INPUT.OTHER) {
            return {
              ...state,
              frequency
            };
          }
          const defaultAmount = findDefaultAmount(presets);
          const otherAmount = getOtherAmount(presets);
          return {
            ...state,
            frequency,
            amount: defaultAmount?.amount,
            activeInput: defaultAmount?.name,
            otherAmountValue: otherAmount ? otherAmount.amount : null,
            otherAmountDisplayValue: otherAmount ? formatOtherAmountValue(otherAmount.amount, currencyCode, userLocale) : ''
          };
        }
      case 'SELECT_DONATION_FREQUENCY':
        {
          const {
            frequency
          } = action;
          return {
            ...state,
            frequency
          };
        }
      case 'SELECT_RECURRING_END_DATE':
        {
          const {
            recurringEndDate
          } = action;
          return {
            ...state,
            recurringEndDate
          };
        }
      case 'SELECT_PRESET_AMOUNT_BUTTON':
        {
          const {
            selectedPreset
          } = action;
          return {
            ...state,
            activeInput: selectedPreset.name,
            amount: selectedPreset.amount,
            error: ''
          };
        }
      case 'SET_OTHER_AMOUNT_DISPLAY_VALUE':
        {
          const displayValue = state.otherAmountValue ? formatOtherAmountValue(Number(state.otherAmountValue), currencyCode, userLocale) : '';
          return {
            ...state,
            otherAmountDisplayValue: displayValue,
            activeInput: state.otherAmountValue ? AMOUNT_INPUT.OTHER : state.activeInput
          };
        }
      case 'SET_OTHER_AMOUNT':
        {
          const {
            amount,
            minDonationAmount,
            maxDonationAmount
          } = action;
          const numericAmount = Number(amount);
          let error = '';
          if (numericAmount === 0) {
            error = t('useDonationBlockReducer.error.emptyDonationAmount', 'Please enter an amount to donate');
          } else if (numericAmount < minDonationAmount) {
            const minValue = formatCurrency({
              amount: minDonationAmount,
              currencyCode: state.currencyCode,
              options: {
                minimumFractionDigits: 2
              },
              locale: state.userLocale
            }).replace(/.00$/, '');
            error = t('useDonationBlockReducer.error.amountGreaterThanOrEqualTo', {
              minValue,
              defaultValue: 'Enter an amount greater than or equal to {{minValue}}'
            });
          } else if (numericAmount > maxDonationAmount) {
            const maxValue = formatCurrency({
              amount: maxDonationAmount,
              currencyCode: state.currencyCode,
              options: {
                minimumFractionDigits: 2
              },
              locale: state.userLocale
            }).replace(/.00$/, '');
            error = t('useDonationBlockReducer.error.amountSmallerThanOrEqualTo', {
              maxValue,
              defaultValue: 'Enter an amount smaller than or equal to {{maxValue}}'
            });
          }
          return {
            ...state,
            otherAmountValue: numericAmount,
            otherAmountDisplayValue: amount,
            amount: numericAmount,
            activeInput: AMOUNT_INPUT.OTHER,
            error
          };
        }
      default:
        return state;
    }
  };
  return useReducer(DonationReducer, {
    donationFrequencies,
    donationAmounts,
    recurringAmounts,
    currencyCode,
    userLocale
  }, createInitialState);
};